<template>
  <v-container>
    <div style="display: none">
      <EditDialog
        v-bind:model="editLimitation"
        v-bind:moduleName="'limitations'"
        v-bind:btnName="''"
        v-bind:formTitle="$t('limitations.label')"
        v-bind:dataValidation="validateLimitation"
        @save="saveEvent"
        @close="cleanupDialog"
        ref="editDialog"
      >
        <template v-slot:edit_content>
          <LimitationForm></LimitationForm>
        </template>
      </EditDialog>
    </div>
    <v-toolbar elevation="1" color="white" class="px-2">
      <v-toolbar-title class="text-h6 font-weight-bold">
        <v-icon size="small" class="mr-2" color="primary">mdi-tortoise</v-icon>
        {{ $filters.capitalize($t(`${moduleName}.label`)) }}
      </v-toolbar-title>

      <v-btn-toggle
        :model-value="isListView ? 'list' : 'calendar'"
        mandatory
        density="compact"
        variant="outlined"
        color="primary"
        class="mr-3"
        @update:model-value="setView"
      >
        <v-btn value="list" prepend-icon="mdi-table" size="small">
          {{ $filters.capitalize($t("limitations.list_view")) }}
        </v-btn>
        <v-btn value="calendar" prepend-icon="mdi-calendar-month" size="small">
          {{ $filters.capitalize($t("limitations.calendar_view")) }}
        </v-btn>
      </v-btn-toggle>

      <v-btn
        color="primary"
        variant="flat"
        prepend-icon="mdi-plus"
        @click="newLimitation"
      >
        {{ $filters.capitalize($t(`${moduleName}.actions.add_item`)) }}
      </v-btn>
    </v-toolbar>

    <LimitationSearch class="mt-2" @filterToP="changeP($event)"/>

    <v-card flat class="mt-2">
      <template v-if="isListView">
        <v-data-table-server
        class="limitation-table"
        :headers="headers"
        :items="visibleRows"
        :sort-by="[{ key: 'start', order: 'desc' }]"
        item-key="pk"
        :items-per-page-text='$t("rows_per_page")'
        :items-per-page-options='itemsPerPageOptions'
        :items-length="listPagination.size || 0"
        :items-per-page="listPagination.page_size"
        :page="listPagination.current"
        :loading="$store.state.limitations.isLoading"
        :row-props="rowProps"
        density="comfortable"
        hover
        fixed-header
        @click:row="onRowClick"
        :no-data-text="$filters.capitalize($t('no_data_text'))"
        :no-results-text="$filters.capitalize($t('no_results_text'))"
        @update:options="loadOptions">

        <!-- producer + status -->
        <template v-slot:item.producer.display_name="{ item }">
          <div class="limitation-producer">
            <span
              class="limitation-origin-bar"
              :style="{ backgroundColor: originColor(item.origin ? item.origin.name : '') }"
            ></span>
            <span class="font-weight-medium">{{ item.producer ? item.producer.display_name : "-" }}</span>
            <v-chip
              :color="limitationStatus(item).color"
              size="x-small"
              label
              variant="tonal"
              class="flex-shrink-0"
            >{{ $filters.capitalize($t(`limitations.status.${limitationStatus(item).key}`)) }}</v-chip>
          </div>
        </template>

        <template v-slot:item.power_limit="{ item }">
          <span v-if="item.power_limit === null || item.power_limit === undefined" class="text-disabled">-</span>
          <span v-else class="font-weight-medium">{{ item.power_limit }} <span class="text-caption text-medium-emphasis">kW</span></span>
        </template>

        <!-- dates: day on one line, time below -->
        <template v-slot:item.start="{ item }">
          <div class="text-no-wrap">{{ $filters.formatDate(item.start) }}</div>
          <div class="text-caption text-medium-emphasis">{{ $filters.formatTime(item.start) }}</div>
        </template>
        <template v-slot:item.end="{ item }">
          <v-chip v-if="!item.end" size="small" color="warning" label variant="tonal" prepend-icon="mdi-timer-sand">
            {{ $filters.capitalize($t("limitations.no_end")) }}
          </v-chip>
          <template v-else>
            <div class="text-no-wrap">{{ $filters.formatDate(item.end) }}</div>
            <div class="text-caption text-medium-emphasis">{{ $filters.formatTime(item.end) }}</div>
          </template>
        </template>
        <template v-slot:item.duration="{ item }">
          <span v-if="!item.end" class="text-disabled">-</span>
          <span v-else class="text-no-wrap">{{ calculateDuration(item.start, item.end) }}</span>
        </template>

        <!-- availability impact: icon instead of yes / no -->
        <template v-slot:item.impact_dispo="{ item }">
          <!-- red when the limitation does NOT count in the availability;
               the explanation (same sentence as the form) in a tooltip -->
          <v-tooltip
            location="top"
            max-width="320"
            :text="$t(`limitations.impact_dispo.${item.impact_dispo ? 'true' : 'false'}`)"
          >
            <template v-slot:activator="{ props }">
              <v-icon
                v-bind="props"
                :color="item.impact_dispo ? 'success' : 'error'"
                size="small"
              >{{ item.impact_dispo ? 'mdi-check-circle-outline' : 'mdi-alert-circle-outline' }}</v-icon>
            </template>
          </v-tooltip>
        </template>

        <template v-slot:item.comment="{ item }">
          <span v-if="item.comment" class="limitation-comment">{{ item.comment }}</span>
          <span v-else class="text-disabled">-</span>
        </template>

        <template v-slot:item.origin="{ item }">
          <v-chip
            size="small"
            label
            variant="flat"
            :color="originColor(item.origin ? item.origin.name : '')"
            class="text-white"
          >{{ item.origin ? item.origin.label : "-" }}</v-chip>
        </template>

        <template v-slot:item.actions="{ item }">
          <div class="limitation-actions">
            <!-- edit: click on the line (see onRowClick) -->
            <v-btn
              v-if="isFromStaff && item.origin.name !== 'deie'"
              icon="mdi-delete-outline"
              size="x-small"
              variant="text"
              color="error"
              :title="$filters.capitalize($t('actions.delete'))"
              @click.stop="deleteBtn(item.pk)"
            ></v-btn>
          </div>
        </template>
      </v-data-table-server>
      </template>
      <template v-else>
        <!-- Legend: one color per origin, click to hide / show it -->
        <div class="calendar-legend d-flex flex-wrap align-center pa-3">
          <span class="text-caption text-medium-emphasis mr-2">
            {{ $filters.capitalize($t("limitations.origin.label")) }}
          </span>
          <v-chip
            v-for="origin in legendOrigins"
            :key="origin.name"
            size="small"
            label
            class="mr-2 mb-1"
            :color="originColor(origin.name)"
            :variant="hiddenOrigins.includes(origin.name) ? 'outlined' : 'flat'"
            :prepend-icon="hiddenOrigins.includes(origin.name) ? 'mdi-eye-off-outline' : 'mdi-circle'"
            @click="toggleOrigin(origin.name)"
          >{{ origin.label }} ({{ origin.count }})</v-chip>
          <span class="legend-open mr-1"></span>
          <span class="text-caption text-medium-emphasis">
            {{ $filters.capitalize($t("limitations.no_end")) }}
          </span>
        </div>
        <CalendarView
          :events="events"
          :locale="$store.getters.language"
          @rangeChange="onCalendarRange"
          @eventClick="openEdit"
        />
      </template>
    </v-card>
  </v-container>
</template>

<script>
import { isNone, capitalize, alertPopup, slicyUpdate, slicyRemove } from "@/functions";
import filters from "@/mixins/filters";
import utils from "@/mixins/utils";
import LimitationForm from "@/components/LimitationForm.vue";
import EditDialog from "@/components/EditDialog";
import CalendarView from "@/components/LimitationCalendarView.vue";
import LimitationSearch from "@/components/limitations/LimitationSearch.vue";

export default {
  mixins: [utils, filters],
  components: { LimitationForm, EditDialog, CalendarView, LimitationSearch },

  methods: {
    changeP(search_params) {
      this.search_params = search_params;
      // New criteria: back to the first page, the old page may not exist
      this.paginationOptions.page = 1;
      this.loadList();
    },
    setView(view) {
      // Each view keeps its own rows (listRows / calendarRows) and asks for
      // a load on mount (the table through update:options, the calendar
      // through rangeChange); loadList skips it when nothing changed.
      this.isListView = view === "list";
    },
    invalidateOtherView() {
      // Rows were added / edited / removed in the current view: the other
      // one reloads next time it is shown
      if (this.isListView) this.calendarKey = null;
      else this.listKey = null;
    },
    toggleOrigin(name) {
      const index = this.hiddenOrigins.indexOf(name);
      if (index === -1) this.hiddenOrigins.push(name);
      else this.hiddenOrigins.splice(index, 1);
    },
    onCalendarRange(range) {
      this.calendarRange = range;
      this.loadList();
    },
    async loadList() {
      const listView = this.isListView;
      // active_now is a front-side flag ("en cours" toggle), not an API parameter
      const { active_now, ...searchParams } = this.search_params;
      let payload;
      if (listView) {
        payload = {
          ...this.paginationOptions,
          ...searchParams
        };
      } else {
        // Calendar: the limitations active during the displayed period
        // (the date range of the search box is ignored, producers and
        // origins apply), capped to keep the calendar responsive.
        // With "en cours" on, the period is "now" whatever month is shown.
        if (isNone(this.calendarRange)) return;
        payload = {
          ...searchParams,
          start: active_now ? searchParams.start : this.calendarRange.start,
          end: active_now ? searchParams.end : this.calendarRange.end,
          page: 1,
          size: this.calendarMaxEvents
        };
      }
      // Same parameters as the rows already loaded for this view (e.g.
      // switching table <-> calendar): nothing to fetch.
      const key = JSON.stringify(payload);
      if (key === (listView ? this.listKey : this.calendarKey)) return;

      // Every call is sent (a change made while a request is in flight used
      // to be dropped); only the latest response is kept.
      const requestId = ++this.loadRequestId;
      let response;
      try {
        response = await this.$store.dispatch('limitations/getItems', payload);
      } catch (e) {
        return;
      }
      if (requestId !== this.loadRequestId) return;
      if (listView) {
        this.listRows = response;
        this.listKey = key;
        this.listPagination = { ...this.$store.state.limitations.pagination };
      } else {
        this.calendarRows = response;
        this.calendarKey = key;
      }
    },

    loadOptions({page, itemsPerPage, sortBy}){
        this.paginationOptions = this.genPaginationPayload(
            {"page": page, "size": itemsPerPage, "sortBy":sortBy})
        this.loadList();
    },

    openEdit(item) {
      Promise.all([
        this.$store.dispatch(`${this.moduleName}/updateEdit`, item),
        this.$store.dispatch("producers/updateEdit", item.producer)
      ]).then(() => {
        this.editLimitation = true;
      });
    },
    deleteBtn(pk) {
      alertPopup(this.$t("dialog.delete_confirm")).then((result) => {
        if(result){
          this.$store.dispatch(`${this.moduleName}/deleteItem`, pk).then(() => {
          this.editLimitation = false;
        });
        const index = this.limitations_data.findIndex((i) => i.pk === pk);
        this.limitations_data.splice(index, 1);
        this.invalidateOtherView();
        }
      });
    },

    saveEvent() {
      let baseItem = JSON.parse(JSON.stringify(this.editedItem))
      baseItem.origin_id = this.editedItem.origin.pk
      delete baseItem.origin

      if (!isNone(this.editedItem.producer_ids)) {
        // Only a new limitation can have multiple producers
        let req_array = [];
        let updatedItems = [];

        for (const p of this.editedItem.producer_ids) {
          const updatedItem = { ...baseItem };
          updatedItem.producer_id = p.pk;
          delete updatedItem.producer;
          updatedItem.producer_ids = null;
          req_array.push(
            this.$store.dispatch(`${this.moduleName}/addItem`, updatedItem)
          );
          updatedItems.push(updatedItem);
        }

        Promise.all(req_array).then((results) => {
          results.forEach((result) => {
            this.addToLimitationData(result);
          });
          this.editLimitation = false;
        })
      } else {
        delete baseItem.producer
        if (isNone(this.editedItem.pk)) {
          // new limitation
          this.$store
            .dispatch(`${this.moduleName}/addItem`, baseItem)
            .then(() => {
              this.editLimitation = false;
            });
            this.addToLimitationData(this.editedItem);

        } else {
          // if there is a pk it is an existing limitation
          this.$store
            .dispatch(`${this.moduleName}/editItem`, this.editedItem)
            .then(() => {
              this.editLimitation = false;
              slicyUpdate(this.limitations_data, this.editedItem);
              this.invalidateOtherView();
            });
        }
      }
    },
    addToLimitationData(item) {
      const search_start = this.search_params.start;
      const search_end = this.search_params.end;
      const search_producer_id = this.search_params.producer_id;
      const search_origin = this.search_params.origin_options || [];
      let rangeIsValid = new Date(item.start) >= search_start && new Date(item.end) <= search_end;
      let matchesProducerId = search_producer_id.includes(item.producer_id);
      let isInOrigin = search_origin.includes(item.origin ? item.origin.name : "");
      if (search_producer_id.length === 0) {
        matchesProducerId = true;
      }
      if (search_origin.length === 0) {
        isInOrigin = true;
      }
      if (search_start == null && search_end == null) {
        rangeIsValid = true;
      }  
      if (rangeIsValid && matchesProducerId && isInOrigin) {
        this.limitations_data.push(item);
        this.invalidateOtherView();
      } else {
        this.$store.dispatch("snackbar/showWarning",
          'Item does not meet the criteria and will not be added.'
        );
      }
    },
    cleanupDialog() {
      // this.$store.dispatch("limitations/cleanupEdit");
      this.editLimitation = false;
    },
    newLimitation() {
      Promise.all([
        this.$store.dispatch("limitations/cleanupEdit"),
        this.$store.dispatch("producers/cleanupEdit")
      ]).then(() => {
        this.editLimitation = true;
      });
    },
    validateLimitation() {
        const lim_ = this.$store.state.limitations.edit
        if (lim_.end && lim_.start > lim_.end) return capitalize(this.$t("limitations.date_error"))
        if (isNone(lim_.producer_id) && isNone(lim_.producer_ids)) {
            return capitalize(this.$t("limitations.missing.producer"))
        }
        if (lim_.start < new Date().setHours(0, 0, 0, 0)) {
            return capitalize(this.$t("limitations.impact_dispo.past"))
        }
        return
    },
    limitationStatus(item) {
      // ended / active (now inside the period, or no end) / upcoming
      const now = new Date();
      const start = new Date(item.start);
      const end = item.end ? new Date(item.end) : null;
      if (start > now) return { key: "upcoming", color: "info" };
      if (end && end < now) return { key: "ended", color: "grey" };
      return { key: "active", color: "success" };
    },
    canEdit(item) {
      return this.isFromStaff && (item.origin.name !== 'deie' || item.end !== undefined);
    },
    onRowClick(event, { item }) {
      // a click on the line opens the edition (no edit button)
      if (this.canEdit(item)) this.openEdit(item);
    },
    rowProps({ item }) {
      // subtle highlight of the limitations running right now, pointer on editable lines
      const classes = [];
      if (this.limitationStatus(item).key === "active") classes.push("limitation-row--active");
      if (this.canEdit(item)) classes.push("limitation-row--editable");
      return { class: classes.join(" ") };
    },
    originColor(name) {
      // One stable color per origin
      const palette = ["#0093bf", "#6b5b4f", "#e67e22", "#8e44ad", "#16a085", "#c0392b", "#2c3e50"];
      let hash = 0;
      for (const c of String(name)) hash = (hash * 31 + c.charCodeAt(0)) % 9973;
      return palette[hash % palette.length];
    },
    calculateDuration(start, end) {
      // Human readable, only the significant units:
      // "10 min", "2 h 30 min", "3 j 4 h", "< 1 min"
      if (!start || !end) return '';
      const diffMinutes = Math.floor(Math.abs(new Date(end) - new Date(start)) / 60000);
      const days = Math.floor(diffMinutes / 1440);
      const hours = Math.floor((diffMinutes % 1440) / 60);
      const minutes = diffMinutes % 60;
      const u = (key) => this.$t(`limitations.duration_units.${key}`);
      if (diffMinutes < 1) return `< 1 ${u("minute")}`;
      if (days > 0) {
        return hours > 0 ? `${days} ${u("day")} ${hours} ${u("hour")}` : `${days} ${u("day")}`;
      }
      if (hours > 0) {
        return minutes > 0 ? `${hours} ${u("hour")} ${minutes} ${u("minute")}` : `${hours} ${u("hour")}`;
      }
      return `${minutes} ${u("minute")}`;
    }
  },
  computed: {
    ...utils.computed,
    limitations_data() {
      // Rows of the current view (the same array is mutated by the local
      // add / edit / delete handlers)
      return this.isListView ? this.listRows : this.calendarRows;
    },
    visibleRows() {
      // Table rows minus the origins hidden through the legend
      return this.limitations_data.filter(
        (item) => !this.hiddenOrigins.includes(item.origin ? item.origin.name : "")
      );
    },
    legendOrigins() {
      // Origins present in the loaded limitations, with their count
      const counts = {};
      for (const item of this.limitations_data) {
        const name = item.origin ? item.origin.name : "";
        const label = item.origin ? item.origin.label : "-";
        if (!counts[name]) counts[name] = { name, label, count: 0 };
        counts[name].count += 1;
      }
      return Object.values(counts).sort((a, b) => b.count - a.count);
    },
    events() {
      // Calendar events, always in sync with the loaded limitations
      return this.limitations_data
        .filter((item) => !this.hiddenOrigins.includes(item.origin ? item.origin.name : ""))
        .map((item) => {
        const producer = item.producer ? item.producer.display_name : "";
        const origin = item.origin ? item.origin.label : "";
        const open = !item.end;
        let title = `${producer} · ${origin} · ${item.power_limit} kW`;
        if (open) title += ` (${this.$t("limitations.no_end")})`;
        return {
          id: item.pk,
          title: title,
          start: item.start,
          // Open-ended: drawn on every day after its start, up to the end
          // of the displayed period
          end: open
            ? (this.calendarRange ? this.calendarRange.end : undefined)
            : item.end,
          color: this.originColor(item.origin ? item.origin.name : ""),
          classNames: open ? ["limitation-open"] : [],
          extendedProps: { limitation: item }
        };
        });
    }
  },
  data() {
    return {
      // One row set per view, with the parameters they were loaded with
      listRows: [],
      listKey: null,
      listPagination: { size: 0, page_size: 100, current: 1 },
      calendarRows: [],
      calendarKey: null,
      itemsPerPageOptions: [10, 20, 50, 100],
      isListView: true,
      moduleName: "limitations",
      editLimitation: false,
      paginationOptions: {
        "page": 1,
        "size": 100,
        "ordering": "-start"
      },
      loadRequestId: 0,
      calendarRange: null,
      calendarMaxEvents: 1000,
      hiddenOrigins: [],

      headers: [
        {
          title: this.$filters.capitalize(this.$t("producers.label", 1)),
          key: "producer.display_name",
          width: "260px",
          sortable: true
        },
        {
          title: this.$filters.capitalize(this.$t("limitations.power_limit")),
          key: "power_limit",
          width: "130px",
          sortable: true,
          align: "center"
        },
        {
          title: this.$filters.capitalize(this.$t("limitations.start")),
          key: "start",
          width: "110px",
          sortable: true
        },
        {
          title: this.$filters.capitalize(this.$t("limitations.end")),
          key: "end",
          width: "150px",
          sortable: true
        },
        {
          title: this.$filters.capitalize(this.$t("limitations.duration")),
          key: "duration",
          width: "110px",
          sortable: false
        },
        {
          title: this.$filters.capitalize(this.$t("limitations.impact_dispo.label")),
          key: "impact_dispo",
          width: "90px",
          sortable: true,
          align: "center"
        },
        {
          title: this.$filters.capitalize(this.$t("limitations.comment")),
          key: "comment",
          sortable: false
        },
        {
          title: this.$filters.capitalize(this.$t("limitations.origin.label")),
          key: "origin",
          width: "120px",
          sortable: true
        },
        {
          title: this.$filters.capitalize(this.$t("actions.label")),
          key: "actions",
          width: "60px",
          sortable: false,
          align: "end"
        }
      ]
    };
  }
};
</script>

<style>
.calendar-legend .v-chip {
  cursor: pointer;
}

/* list view */
.limitation-table .v-data-table__th {
  text-transform: uppercase;
  font-size: 0.72rem;
  letter-spacing: 0.04em;
  white-space: nowrap;
}
.limitation-table tbody tr:nth-child(even) {
  background-color: rgba(0, 0, 0, 0.015);
}
.limitation-table tr.limitation-row--active {
  background-color: rgba(76, 175, 80, 0.06);
}
.limitation-table tr.limitation-row--editable {
  cursor: pointer;
}
.limitation-producer {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.limitation-origin-bar {
  display: inline-block;
  width: 4px;
  height: 22px;
  border-radius: 2px;
  flex-shrink: 0;
}
.limitation-comment {
  display: block;
  white-space: normal;
  word-break: break-word;
  min-width: 160px;
  font-size: 0.85rem;
}
.limitation-actions {
  display: inline-flex;
  gap: 2px;
  white-space: nowrap;
  opacity: 0.7;
}
tr:hover .limitation-actions {
  opacity: 1;
}

.legend-open {
  /* same look as an open-ended block in the calendar */
  display: inline-block;
  width: 26px;
  height: 14px;
  border-radius: 3px;
  background-color: #888;
  background-image: repeating-linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.35) 0 4px,
    transparent 4px 8px
  );
}
</style>
